import React, { Fragment, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import store from "store";
import { getCfdQuoteCurrencyUpper } from "@/components/Pages/Market/MarketList/cfdTabListSource";
import { isFiatQuoteCurrency } from "@/utils/getFiatCurrencyMap";

import XtSvg from "@/components/Xt/Svg";
import tabCfgAry from "./tabCfgAry";
import CMPT_Zone from "./zone";
import CMPT_Group from "./group";

import styles from "./index.module.scss";

const { useTranslation } = Hooks;

export interface TabCfgProps {
  key: string;
  label?: string;
  labelKey?: string;
  buyCoinAry?: string[];
  group?: string;
  plateId?: number;
  noHidden?: boolean;
}

interface Props {
  tabCfg: TabCfgProps;
  tabChange: (item: TabCfgProps) => void;
}

const Main: React.FC<Props> = ({ tabCfg, tabChange }) => {
  const t = useTranslation();
  const { isLogin } = store.user;
  const router = useRouter();

  const isCfd = useMemo(() => {
    const byRoute = router.pathname?.startsWith("/futures/") || router.asPath?.startsWith("/futures/");
    const byStore = !!store.market.name && store.market.name.toLowerCase().endsWith("_cfd");
    return byRoute || byStore;
  }, [router.pathname, router.asPath, store.market.name]);

  const isFiatContract = useMemo(() => {
    if (!isCfd) return false;
    const { name } = store.market;
    if (!name) return false;
    return isFiatQuoteCurrency(getCfdQuoteCurrencyUpper(name));
  }, [isCfd, store.market.name]);

  const tabCfgAryFormat = useMemo(() => {
    const ary: any[] = [];
    const doc = {};

    let filteredTabCfgAry: typeof tabCfgAry;

    if (!isCfd) {
      filteredTabCfgAry = tabCfgAry.filter((obj) => obj.key !== "fiat");
    } else if (isFiatContract) {
      filteredTabCfgAry = tabCfgAry.filter(
        (obj) => obj.key === "user" || obj.key === "fiat" || obj.key === "stocks" || obj.key === "commodities"
      );
    } else {
      filteredTabCfgAry = tabCfgAry.filter(
        (obj) => obj.key === "user" || obj.key === "usds" || obj.key === "stocks" || obj.key === "commodities"
      );
    }

    filteredTabCfgAry.map((obj) => {
      const { group } = obj;
      if (!group) return ary.push(obj);
      if (!doc[group]) {
        doc[group] = {
          group,
          children: [],
        };
        ary.push(doc[group]);
      }
      doc[group].children.push(obj);
    });

    return ary;
  }, [isCfd, isFiatContract]);

  return (
    <div className={styles.main}>
      {tabCfgAryFormat.map((item) => {
        if (item.key === "user") {
          if (isLogin) {
            return (
              <button key={item.key} className={cx("btnTxt", styles.user, { [styles.atv]: tabCfg.key === "user" })} onClick={() => tabChange(item)}>
                <XtSvg icon={`star-new-filled`} />
              </button>
            );
          }
          return <Fragment key={item.key}></Fragment>;
        }

        if (item.group) {
          return (
            <CMPT_Group key={item.group} tabCfg={tabCfg} groupCfg={item} tabChange={tabChange} className={cx({ [styles.atv]: tabCfg.group === item.group })} />
          );
        }

        if (item.key === "zone") {
          return <CMPT_Zone key={item.key} tabCfg={tabCfg} tabChange={tabChange} className={cx({ [styles.atv]: tabCfg.key === "zone" })} />;
        }

        return (
          <button key={item.key} className={cx("btnTxt", { [styles.atv]: tabCfg.key === item.key })} onClick={() => tabChange(item)}>
            {item.labelKey ? t(item.labelKey) : item.label}
          </button>
        );
      })}
    </div>
  );
};

export default observer(Main);
